import { createSelector } from 'reselect';
import { RootState } from '../../../types';
import { EntityTypes } from '../../../constants/entities';
import { IEntityReducer } from 'core/reducers/entities';
import {
  CourseNodeRole,
  CourseNodeType,
  ICourseNode,
  ICourseNodeDAO,
  ICourseOut,
  IModuleCourseNode,
} from 'types/domain/course';
import { getEntities } from '../utils/getEntities';
import { parseVerification } from './verifications';
import { parseModule } from '../product/module';
import { parseCourseNodesAndEdges } from './flow';
import { Node } from 'reactflow';
import { createCachedSelector } from 're-reselect';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id?: number) => (id ? id : state.state[EntityTypes.COURSE]);

export const selectModuleCourseNodes = createCachedSelector(entity, id, (entity: IEntityReducer, id: number) => {
  const flowData = parseCourseNodesAndEdges(entity, id, true);

  return flowData.nodes
    .map((node: Node) => entity[EntityTypes.COURSE_NODE][node.data.id])
    .filter((courseNode: ICourseNode) => !!courseNode.moduleId)
    .map((courseNode: ICourseNode) => {
      return {
        ...courseNode,
        node: courseNode.nodeId ? entity[EntityTypes.NODE][courseNode.nodeId] : null,
        text: courseNode.textId ? entity[EntityTypes.TEXT][courseNode.textId] : null,
        image: courseNode.imageId ? entity[EntityTypes.IMAGE][courseNode.imageId] : null,
        module: parseModule(entity, courseNode.moduleId),
      } as IModuleCourseNode;
    });
})((state: RootState, id?: number) => id || state.state[EntityTypes.COURSE] || 'unknown');

export function parseCourseNodeDAO(entity, id) {
  const courseNode: ICourseNode = entity[EntityTypes.COURSE_NODE][id];

  if (!courseNode) return null;

  const outs = getEntities(entity, EntityTypes.COURSE_OUT, 'sourceId', courseNode.id);

  return {
    ...courseNode,
    text: courseNode.textId ? entity[EntityTypes.TEXT][courseNode.textId] : null,
    image: courseNode.imageId ? entity[EntityTypes.IMAGE][courseNode.imageId] : null,
    entities: getEntities(entity, EntityTypes.COURSE_NODE_ENTITY, 'courseNodeId', courseNode.id).map(entity => {
      return { ...entity, outs: outs.filter((out: ICourseOut) => out.sourceHandle === entity.id) };
    }),
    outs,
    variable: courseNode.variableId ? entity[EntityTypes.VARIABLE][courseNode.variableId] : null,
    verification: courseNode.verificationId
      ? parseVerification(entity, courseNode.verificationId, courseNode.id)
      : null,
  } as ICourseNodeDAO;
}

export const selectStartCourseNodes = createCachedSelector(entity, id, (entity: IEntityReducer, id: number) => {
  return getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', id)
    .filter(
      (courseNode: ICourseNode) =>
        courseNode.status > -1 &&
        courseNode.type === CourseNodeType.START
       //&& [CourseNodeRole.SCORM, CourseNodeRole.SHARE, CourseNodeRole.API].includes(courseNode.entityType),
    )
    .map((courseNode: ICourseNode) => {
      return {
        ...courseNode,
        node: courseNode.nodeId ? entity[EntityTypes.NODE][courseNode.nodeId] : null,
        text: courseNode.textId ? entity[EntityTypes.TEXT][courseNode.textId] : null,
        image: courseNode.imageId ? entity[EntityTypes.IMAGE][courseNode.imageId] : null,
      } as ICourseNodeDAO;
    });
})((state: RootState, id?: number) => id || state.state[EntityTypes.COURSE] || 'unknown');
